import { useCallback, useState } from "react";
import { useScope } from "@state/scopeContext.lib";
import { useEntities } from "@hooks/useEntities";
import { useSimilarTransactionsNewValue } from "../SimilarTransactionsNewValueContext";
import { useActionsContext } from "./ActionsContext";
import {
    buildDeleteActionReassignments,
    buildDeleteBaseActionReassignments,
    DeleteActionReassignment,
} from "./deleteActionPlanner";
import { fetchMatchingTransactionIdsForConditions } from "./rowMatching";
import { ActionRowDefinition } from "./types";

interface UseDeleteActionRowResult {
    deleteActionRow: () => Promise<void>;
    isDeleting: boolean;
}

export function useDeleteActionRow(
    row: ActionRowDefinition,
): UseDeleteActionRowResult {
    const { workspaceId } = useScope();
    const {
        rows,
        rowIdToOwnedTxIds,
        matchingScopeConditions,
        valueToAssign,
        filters,
        removeRow,
    } = useActionsContext();
    const { newValuesMap, setNewValues } = useSimilarTransactionsNewValue();
    const entities = useEntities({ excludeMockEntities: true });
    const [isDeleting, setIsDeleting] = useState(false);

    const deleteActionRow = useCallback(async () => {
        if (isDeleting) {
            return;
        }

        setIsDeleting(true);
        const deleteRow = async () => {
            let assignments: DeleteActionReassignment[] = [];
            if (row.isBaseAction) {
                assignments = buildDeleteBaseActionReassignments({
                    rows,
                    deletedRowId: row.id,
                    newValuesMap,
                    valueToAssign,
                    entities,
                });
            } else if (workspaceId) {
                assignments = await buildDeleteActionReassignments({
                    rows,
                    deletedRowId: row.id,
                    rowIdToOwnedTxIds,
                    scopeConditions: matchingScopeConditions,
                    valueToAssign,
                    entities,
                    fetchMatchingTransactionIdsForConditions: async (
                        conditions,
                    ) =>
                        await fetchMatchingTransactionIdsForConditions({
                            workspaceId,
                            conditions,
                            excludedTransactionIds:
                                filters.excludedTransactionIds,
                        }),
                });
            }

            if (assignments.length > 0) {
                setNewValues(assignments);
            }

            removeRow(row.id);
        };
        await deleteRow().finally(() => {
            setIsDeleting(false);
        });
    }, [
        isDeleting,
        row,
        removeRow,
        workspaceId,
        rows,
        newValuesMap,
        rowIdToOwnedTxIds,
        matchingScopeConditions,
        valueToAssign,
        entities,
        filters.excludedTransactionIds,
        setNewValues,
    ]);

    return { deleteActionRow, isDeleting };
}
